import React, { Dispatch, SetStateAction, useState } from 'react'
import { Button, ColorType, Link, Text } from '@clear/design-system'
import { useForm } from 'react-hook-form'
import { Form, StepSubTitle, StepTitle } from '@components/layout/Form'
import { verificationCodeSchema } from '@validators/schema/enroll/authSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import OTPInput from './OTPInput'
import { min } from 'lodash'

type InitiateAuth = {
  phoneNumber?: string
  email?: string
  otpSessionId: string
}

interface VerificationCodeFormProps {
  formData: {
    email?: string
    phoneNumber?: string
    otpSessionId?: string
  }
  showLogo?: boolean
  otpFailureMessage?: string
  setOtpFailureMessage?: Dispatch<SetStateAction<string>>
  isLoading?: boolean
  onSubmit: (formData: VerificationCodeFormProps) => void
  onSubmitContact?: (formData: InitiateAuth) => void
  verificationFormData?: InitiateAuth
}

interface FormValues {
  verificationCode: string
}

const VerificationCodeForm: React.FC<VerificationCodeFormProps> = ({
  showLogo = true,
  formData,
  onSubmit,
  otpFailureMessage,
  setOtpFailureMessage,
  isLoading,
  onSubmitContact,
  verificationFormData,
}) => {
  const { handleSubmit, formState, register, setValue, watch } =
    useForm<FormValues>({
      resolver: yupResolver(verificationCodeSchema),
      mode: 'onChange',
      defaultValues: {
        verificationCode: ''.padEnd(6, ''),
      },
    })
  const [codeHasSent, setCodeHasSent] = useState(false)
  const { isValid } = formState
  const otpValue = watch('verificationCode')
  function clearOtpFailureMessage() {
    if (setOtpFailureMessage) {
      setOtpFailureMessage(undefined)
    }
  }

  async function onFormSubmit(formData: any) {
    clearOtpFailureMessage()

    onSubmit(formData)
  }

  const SemiBoldText = ({ children }) => {
    return <Text fontWeight="600">{children}</Text>
  }
  const maskEmail = (email: string) => {
    const [local, domain] = email.split('@')
    if (local.length <= 3) {
      return (
        <SemiBoldText>
          {local[0]}---@{domain}
        </SemiBoldText>
      )
    }
    return (
      <SemiBoldText>
        {local[0]}
        {'-'.repeat(min([local.length - 3, 6]))}
        {local.slice(-2)}@{domain}
      </SemiBoldText>
    )
  }

  const maskPhoneNumber = (phoneNumber: string) => {
    const lastFour = phoneNumber.slice(-4)
    return <SemiBoldText>(---) ---{lastFour}</SemiBoldText>
  }

  const renderText = () => {
    const { email, phoneNumber } = formData || {}
    const maskedEmail = email ? maskEmail(email) : null
    const maskedPhone = phoneNumber ? maskPhoneNumber(phoneNumber) : null

    if (maskedEmail && maskedPhone) {
      return (
        <>
          We sent a temporary code to {maskedEmail} and {maskedPhone}. Please
          enter the code below.
        </>
      )
    }
  }

  const handleResendClick = () => {
    onSubmitContact(verificationFormData)
    clearOtpFailureMessage()
    setValue('verificationCode', '', { shouldValidate: true })
    setCodeHasSent(true)
  }

  return (
    <>
      {showLogo && (
        <img
          data-testid="auth-inset-precheck-logo"
          className="pb-5 mx-auto"
          src="tsa-pre-logo-auth.svg"
          alt="TSA pre-check with CLEAR logo"
        />
      )}
      <StepTitle
        data-testid="verification-code-inset-title"
        mt={0}
        textAlign="center"
        color={'blackNeutral' as ColorType}
        fontSize={5}
      >
        Enter Verification Code
      </StepTitle>

      <StepSubTitle
        data-testid="verification-code-helper-text"
        fontSize={2}
        textAlign="center"
      >
        {renderText()}
      </StepSubTitle>

      <Form onSubmit={handleSubmit(onFormSubmit)} mt={5}>
        <OTPInput
          name="verificationCode"
          register={register}
          setValue={setValue}
          watch={watch}
          value={otpValue}
        />

        <Text
          data-testid="verification-code-failure-message"
          mt={3}
          textAlign="center"
          color="utilityDarkErrorRed"
        >
          {otpFailureMessage}
        </Text>
        {onSubmitContact && verificationFormData && !codeHasSent ? (
          <Link
            data-testid="verification-code-resend-otp-button"
            mt={5}
            id="authv-resend-otp-btn"
            onClick={handleResendClick}
            data-cy="authv-resend-otp-btn"
            textAlign="center"
            border="none"
          >
            Resend Code
          </Link>
        ) : (
          <Link
            data-testid="verification-code-resent-button"
            mt={5}
            id="authv-resend-otp-btn"
            data-cy="authv-resend-otp-btn"
            border="none"
            textAlign="center"
          >
            Code has been resent
          </Link>
        )}
        <Button
          mt={5}
          type="submit"
          text="Next"
          id="authv2-verify-otp-btn"
          variant="primary"
          disabled={!isValid || isLoading}
          color="neutralsWhite"
          data-cy="authv2-verify-otp-btn"
        />
      </Form>
    </>
  )
}

export default VerificationCodeForm
